import { EditableContent } from '../../../edit/EditableContent';
import { pickBlockField } from '../../../lib/localized';
import type { BlockRenderProps } from '../../../types';

function parseStripItems(raw: string): { name: string; price: string }[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/\s+[—–-]\s+/);
      if (parts.length >= 2) {
        return { name: parts[0]!.trim(), price: parts.slice(1).join(' — ').trim() };
      }
      return { name: line, price: '' };
    });
}

/** Wide truck chalkboard — page measure, two-column dish/price board (not the narrow strip). */
export function SimpleMenuBoard({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const itemsRaw = pickBlockField(block.content, 'items', locale);
  const items = parseStripItems(itemsRaw);
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-menu-board', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-menu-board__inner">
        <header className="ws-menu-board__intro">
          {supporting || editable ? (
            <EditableContent
              fieldKey="supporting"
              editable={editable}
              activeEditTarget={target}
              className="ws-menu-board__eyebrow"
            >
              {supporting}
            </EditableContent>
          ) : null}
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-board__title"
          >
            {sectionTitle}
          </EditableContent>
        </header>
        {editable ? (
          <EditableContent
            fieldKey="items"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-board__editor"
          >
            {itemsRaw}
          </EditableContent>
        ) : (
          <ul className="ws-menu-board__list">
            {items.map((item, index) => (
              <li key={`${item.name}-${index}`} className="ws-menu-board__item">
                <span className="ws-menu-board__name">{item.name}</span>
                {item.price ? <span className="ws-menu-board__leader" aria-hidden="true" /> : null}
                {item.price ? <span className="ws-menu-board__price">{item.price}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
